import React, { useContext, useMemo } from 'react';

import TextArea from 'antd/lib/input/TextArea';
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md';
import { Space, Tag } from 'antd';
import useSwr from '@hooks/useSwr';

import SessionContext from '@providers/Session/SessionContext';
import { motProducts } from '@utils/applicationsConstants';
import useUpload from '../Upload';

interface IActivationUploadStep {
  selectedAcronym: string;
  translate: any;
  fileOrManualUploadState: any;
  stringManualUploadState: any;
  actionKey: any;
  setUploadedDevices: any;
  setManualUpload: any;
}

const useActivationUploadStep = ({
  selectedAcronym,
  translate,
  fileOrManualUploadState,
  stringManualUploadState,
  actionKey,
  setUploadedDevices,
  setManualUpload,
}: IActivationUploadStep) => {
  const [fileOrManualUpload, setfileOrManualUpload] = fileOrManualUploadState;
  const [stringManualUpload, setStringManualUpload] = stringManualUploadState;
  const { selectedApp } = useContext(SessionContext);
  const { data: operatorsData } = useSwr('/service-proxy/mot-operators');

  const customInfo = selectedApp && motProducts.includes(selectedApp) && (
    <div>
      <p>{translate('availabilities-operators')}</p>
      <Space size={4} wrap>
        {operatorsData?.map((item: any) => (
          <Tag>{item?.baseName}</Tag>
        ))}
      </Space>
    </div>
  );

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey,
    setUploadedDevices,
    selectedAcronym,
    customInfo,
  });

  return useMemo(() => {
    return {
      uploadReinitState,
      step: {
        ...uploadStep,
        content: (
          <>
            <div style={{ display: 'flex', paddingTop: '10px' }}>
              <div className="activation-upload-step">
                <div
                  className="activation-upload-step-option"
                  onClick={() => {
                    setStringManualUpload('');
                    setfileOrManualUpload(0);
                    setManualUpload([]);
                  }}
                >
                  <span className="radio-button">
                    {!fileOrManualUpload && (
                      <MdRadioButtonChecked style={{ fontSize: '1rem' }} />
                    )}
                    {!!fileOrManualUpload && (
                      <MdRadioButtonUnchecked style={{ fontSize: '1rem' }} />
                    )}
                  </span>
                  <span
                    style={{
                      flex: '0 1 0px',
                      textAlign: 'center',
                    }}
                  >
                    {translate('file-upload')}
                  </span>
                </div>
                <div
                  style={{ height: '1px', background: '#eee', width: '80%' }}
                />
                {/* =================================== */}
                {/* RADIO BUTTON MANUAL ACTIVATION      */}
                {/* =================================== */}

                <div
                  className="activation-upload-step-option  "
                  onClick={() => {
                    uploadReinitState();
                    setfileOrManualUpload(1);
                  }}
                >
                  <span className="radio-button">
                    {!!fileOrManualUpload && (
                      <MdRadioButtonChecked style={{ fontSize: '1rem' }} />
                    )}
                    {!fileOrManualUpload && (
                      <MdRadioButtonUnchecked style={{ fontSize: '1rem' }} />
                    )}
                  </span>
                  <span
                    style={{
                      flex: '0 1 0px',
                      textAlign: 'center',
                    }}
                  >
                    {translate('manual-upload')}
                  </span>
                </div>
              </div>

              {!fileOrManualUpload && (
                <div style={{ flex: '1', height: '100%' }}>
                  {/* ORIGINAL UPLOAD STEP CONTENT SHARED WITH ALL ACTIONS */}
                  {uploadStep.content}
                </div>
              )}
              {!!fileOrManualUpload && (
                <div style={{ flex: '1', height: '100%' }}>
                  {/* INCLUSION OF MANUAL UPLOAD FOR THIS STEP ONLY */}
                  <div
                    className="activation-upload-step-description"
                    style={{ marginBottom: '5px' }}
                  >
                    {translate(
                      selectedAcronym === 'MOT'
                        ? 'manual-instructions.mot'
                        : 'manual-instructions.multiconnect',
                    )}
                  </div>
                  <TextArea
                    id="textarea-iccids"
                    autoSize={{ minRows: 8, maxRows: 8 }}
                    style={{ resize: 'none' }}
                    value={stringManualUpload}
                    placeholder={
                      selectedAcronym === 'MOT'
                        ? '89553202000000159564;5503404446239;724320200047911;TIM\n89553202000000159564;5503404446239;724320200047912;Vivo'
                        : '89553202000000159564;89553202000000159566;'
                    }
                    onInput={(e: any) => {
                      setStringManualUpload(e.target.value);
                    }}
                    onBlur={() => {
                      let flagOK = true;

                      if (selectedAcronym === 'MOT') {
                        const inputClean = stringManualUpload.replaceAll(
                          / |\f|\r|\t|\v|\u00a0|\u1680|\u2000-\u200a|\u2028|\u2029|\u202f|\u205f|\u3000|\ufeff/g,
                          '',
                        ); // empty spaces

                        const inutCleanByLine =
                          inputClean.match(/[^\n]+/g) ?? []; // separate \n
                        const result = [] as Array<any>;

                        inutCleanByLine.forEach((semiColonSeparared: any) => {
                          const line = semiColonSeparared.match(/[^;]+/g) ?? []; // separate by ;
                          if (line.length >= 3) {
                            if (line.length === 3) {
                              result.push({
                                iccid: line[0],
                                msisdn: line[1],
                                vendor: line[2],
                              });
                            }
                            if (line.length === 4) {
                              result.push({
                                iccid: line[0],
                                msisdn: line[1],
                                vendor: line[3],
                                imsi: line[2],
                              });
                            }
                          } else {
                            flagOK = false;
                          }
                        });
                        if (flagOK) {
                          setManualUpload(result);
                        } else {
                          setManualUpload([]);
                        }
                      } else if (selectedAcronym === 'MULTICONNECT') {
                        const input = stringManualUpload.replaceAll(/\s/g, '');
                        const result = input.match(/[^;]+/g) ?? [];
                        result.forEach((el: any) => {
                          if (!/^\d+$/.test(el) || el.length !== 20) {
                            flagOK = false;
                          }
                        });
                        if (flagOK) {
                          setManualUpload(result);
                        } else {
                          setManualUpload([]);
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </>
        ),
      },
    };
  }, [
    uploadReinitState,
    uploadStep,
    translate,
    selectedAcronym,
    fileOrManualUpload,
    stringManualUpload,
    setStringManualUpload,
    setfileOrManualUpload,
    setManualUpload,
  ]);
};

export default useActivationUploadStep;
